import React from 'react';
import PropTypes from 'prop-types';
import errorLogger from '../services/errorLogger';
import errorHandler from '../utils/errorHandler';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({
      error,
      errorInfo
    });

    // Log to error service
    errorLogger.error(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown',
      retryCount: this.state.retryCount
    });

    // Show user-friendly error notification
    errorHandler.showError(
      'Something went wrong. Please try refreshing the page.',
      'error',
      { persistent: true }
    );
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary__content">
            <div className="error-boundary__icon" aria-hidden="true">
              ⚠️
            </div>
            
            <h2 className="error-boundary__title">
              Oops! Something went wrong
            </h2>
            
            <p className="error-boundary__message">
              We're sorry, but something unexpected happened. 
              {this.state.retryCount > 0 && (
                <span> This is attempt #{this.state.retryCount + 1}.</span>
              )}
            </p>

            {!this.props.hideDetails && this.state.error && (
              <details className="error-boundary__details">
                <summary className="error-boundary__summary">
                  Technical Details
                </summary>
                <div className="error-boundary__error-info">
                  <h4>Error:</h4>
                  <pre className="error-boundary__error-message">
                    {this.state.error.toString()}
                  </pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <h4>Component Stack:</h4>
                      <pre className="error-boundary__component-stack">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="error-boundary__actions">
              <button
                className="error-boundary__button error-boundary__button--primary"
                onClick={this.handleRetry}
                aria-label="Try again"
              >
                Try Again
              </button>
              
              <button
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleReload}
                aria-label="Reload page"
              >
                Reload Page
              </button>
            </div>

            {this.props.showReportButton && (
              <button
                className="error-boundary__button error-boundary__button--tertiary"
                onClick={() => this.reportError()}
                aria-label="Report this error"
              >
                Report This Error
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  reportError = () => {
    const errorReport = {
      error: this.state.error?.toString(),
      componentStack: this.state.errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      retryCount: this.state.retryCount
    };

    // In a real application, this would send to a bug reporting service
    errorLogger.info('User reported error', errorReport);
    errorHandler.showSuccess('Error report sent. Thank you for your feedback!');
  };
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
  name: PropTypes.string,
  hideDetails: PropTypes.bool,
  showReportButton: PropTypes.bool
};

ErrorBoundary.defaultProps = {
  hideDetails: false,
  showReportButton: true
};

export default ErrorBoundary;
